import React, { useEffect, useState, useRef } from 'react';
import { GlassCard } from '../../_shared/GlassCard';
import { Footer } from '../../_shared/Footer';
import { Eye, Flame, FileText, TrendingUp, Layers, Zap, Video, ArrowRight, Check } from 'lucide-react';

// ---- Grounded figures (measured Jul 7 2026; see reports/abhiveer/final/data.js) ----
const PROOF = {
    views: 1193611,
    topPage: 598481,
    pieces: 2134,
    bestMonth: 675645,
    accounts: 11,
    days: 105,
    avgViewsPerPiece: 559,
};

const RAMP = [
    { label: 'Mar 24 – Apr 30', tag: 'Launch', posts: 69, kind: 'measured' },
    { label: 'May', tag: 'Breakout', posts: 393, kind: 'measured' },
    { label: 'June', tag: 'Record', posts: 818, kind: 'measured' },
    { label: 'Phase 2', tag: 'Target', posts: 1200, kind: 'projected' },
];
const maxRamp = Math.max(...RAMP.map(r => r.posts));

const PILLARS = [
    { icon: Video, color: 'bg-purple-500/20 text-purple-300', label: 'Main page', from: '~2', to: '5', unit: 'videos / day', body: 'Your flagship stops being the bottleneck. Five fresh pieces a day means the main page compounds on its own — not just off the network.' },
    { icon: Zap, color: 'bg-amber-500/20 text-amber-300', label: 'Every fan page', from: '~3', to: '7', unit: 'videos / day', body: 'Full cadence on the recycling pipeline. The 90-episode backlog is already built — Phase 2 just stops throttling how fast it ships.' },
    { icon: Layers, color: 'bg-pink-500/20 text-pink-300', label: 'Network size', from: '4', to: '6', unit: 'pages', body: 'Two new personas on the proven incubation playbook — 5 fan pages, each feeding the main brand across three platforms.' },
];

const INCLUDES = [
    ['Main page — 5 videos / day', 'Fresh + recycled, your flagship at full pace.'],
    ['5 fan pages — 7 videos / day each', 'Full-throttle recycling network.'],
    ['2 brand-new personas built & incubated', 'On the proven playbook.'],
    ['Omni-channel distribution', 'Instagram Reels · YouTube Shorts · TikTok.'],
    ['Recycling pipeline on 90+ episodes', 'Editing, hooks, formatting.'],
    ['Dedicated account manager + brand-deal routing', ''],
    ['Backend analytics + a monthly network report', 'Like the one you just saw.'],
];

const fmt = (n) => n.toLocaleString('en-US');

const useCounter = (end, duration = 1300) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const done = useRef(false);
    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting && !done.current) {
                done.current = true;
                const t0 = performance.now();
                const step = (now) => {
                    const p = Math.min(1, (now - t0) / duration);
                    setCount(Math.floor(end * (1 - Math.pow(1 - p, 3))));
                    if (p < 1) requestAnimationFrame(step);
                };
                requestAnimationFrame(step);
            }
        }, { threshold: 0.3 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const Stat = ({ icon: Icon, color, label, value, suffix = '', sub }) => {
    const [v, ref] = useCounter(value);
    return (
        <GlassCard className="flex flex-col justify-between min-h-[170px]">
            <div ref={ref}>
                <div className={`${color} p-3 rounded-xl mb-4 inline-flex`}><Icon size={20} /></div>
                <p className="text-white/50 text-[11px] uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-3xl md:text-4xl font-black text-white tracking-tight">{fmt(v)}{suffix}</p>
                {sub && <p className="text-white/40 text-xs mt-3">{sub}</p>}
            </div>
        </GlassCard>
    );
};

export default function AbhiveerPhase2Proposal() {
    useEffect(() => {
        document.title = 'Abhiveer Network — Phase 2 Proposal | SocialSEO';
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white relative overflow-hidden" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');`}</style>

            {/* Ambient background */}
            <div className="fixed inset-0 -z-10 pointer-events-none">
                <div className="absolute -top-40 -left-32 w-[46rem] h-[46rem] rounded-full bg-purple-600/20 blur-[130px]" />
                <div className="absolute top-10 right-0 w-[38rem] h-[38rem] rounded-full bg-emerald-500/10 blur-[130px]" />
            </div>

            {/* Nav */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="font-bold tracking-tight text-purple-300"><span className="text-white">Social</span>SEO</span>
                </div>
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="text-sm font-medium text-white/80">Network · Phase 2</span>
                </div>
            </header>

            <main className="relative z-10">
                {/* HERO */}
                <section className="min-h-[82vh] flex flex-col justify-center items-center text-center px-4 pt-28 pb-12">
                    <div className="max-w-5xl mx-auto w-full space-y-6">
                        <div className="inline-flex items-center px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-md">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse" />
                            <span className="text-sm font-medium text-emerald-300 tracking-wide">Phase 1 delivered · 1.19M views in 105 days</span>
                        </div>
                        <div className="space-y-2">
                            <h2 className="text-lg md:text-xl text-white/60 font-medium">The Mask with Abhiveer — Full Distribution Network</h2>
                            <p className="text-sm text-white/40 font-mono">Prepared for Abhiveer · Proposed term: 3 months</p>
                        </div>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02]">
                            The network works.<br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-white to-purple-300">Now we double it.</span>
                        </h1>
                        <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto">
                            Phase 1 built the machine — 11 accounts, 3 personas, a recycling engine that turned your
                            podcast backlog into 1.19&nbsp;million views. Phase 2 opens the throttle: two new fan pages,
                            the main page to 5 videos a day, every page firing at full cadence.
                        </p>
                    </div>
                </section>

                {/* PROOF STATS */}
                <section className="py-14 px-4">
                    <div className="container mx-auto max-w-6xl grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Stat icon={Eye} color="bg-purple-500/20 text-purple-300" label="Views · 105 days" value={PROOF.views} sub="across the full network" />
                        <Stat icon={TrendingUp} color="bg-blue-500/20 text-blue-300" label="Top fan page" value={PROOF.topPage} sub="@unseenabhiveer alone" />
                        <Stat icon={FileText} color="bg-pink-500/20 text-pink-300" label="Pieces published" value={PROOF.pieces} sub={`${PROOF.accounts} live accounts`} />
                        <Stat icon={Flame} color="bg-amber-500/20 text-amber-300" label="Best month" value={PROOF.bestMonth} sub="May — the breakout" />
                    </div>
                </section>

                {/* PROOF NARRATIVE + RAMP */}
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-4xl">
                        <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold text-center mb-3">What Phase 1 Proved</p>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">Views compounded. Output is the lever we control.</h2>
                        <p className="text-white/50 text-center mb-12 max-w-2xl mx-auto">
                            The network's job is reach for brand deals — and it delivered. The honest read from the data:
                            we can't promise virality, but we <span className="text-white/80 font-semibold">can</span> promise volume,
                            and views follow the network's measured average. So Phase 2 is a volume decision.
                        </p>

                        <GlassCard>
                            <p className="text-white/40 text-[11px] uppercase tracking-widest font-semibold mb-6">Monthly output — pieces published per period</p>
                            <div className="space-y-5">
                                {RAMP.map((r, i) => {
                                    const pct = Math.max(5, Math.round((r.posts / maxRamp) * 100));
                                    const projected = r.kind === 'projected';
                                    return (
                                        <div key={i}>
                                            <div className="flex items-baseline justify-between mb-2 gap-3">
                                                <div className="flex items-center gap-3">
                                                    <p className="text-white font-bold text-sm tracking-tight">{r.label}</p>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${projected ? 'bg-emerald-500/15 text-emerald-300' : 'bg-purple-500/15 text-purple-300'}`}>{r.tag}</span>
                                                </div>
                                                <p className={`font-black text-lg tracking-tight ${projected ? 'text-emerald-300' : 'text-white'}`}>{fmt(r.posts)}<span className="text-white/40 text-xs font-semibold"> pieces</span></p>
                                            </div>
                                            <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
                                                <div className={`h-full rounded-full ${projected ? 'bg-gradient-to-r from-emerald-500/70 to-emerald-300/70' : 'bg-gradient-to-r from-purple-500/60 to-blue-400/60'}`} style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-white/30 text-xs mt-6">Measured, dated posts. Phase 2 is a projected target based on the cadence below.</p>
                        </GlassCard>
                    </div>
                </section>

                {/* THE PHASE 2 MOVE */}
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-6xl">
                        <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold text-center mb-3">The Phase 2 Move</p>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">Three throttles, opened at once.</h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {PILLARS.map((p, i) => {
                                const Icon = p.icon;
                                return (
                                    <GlassCard key={i}>
                                        <div className={`${p.color} p-3 rounded-xl mb-4 inline-flex`}><Icon size={22} /></div>
                                        <p className="text-white/50 text-[11px] uppercase tracking-widest font-semibold mb-3">{p.label}</p>
                                        <div className="flex items-baseline gap-2.5 mb-1">
                                            <span className="text-xl text-white/35 line-through decoration-white/25">{p.from}</span>
                                            <ArrowRight size={18} className="text-white/40" />
                                            <span className="text-4xl font-black text-white tracking-tight">{p.to}</span>
                                            <span className="text-sm text-white/60 font-semibold">{p.unit}</span>
                                        </div>
                                        <p className="text-white/50 text-sm leading-relaxed mt-3">{p.body}</p>
                                    </GlassCard>
                                );
                            })}
                        </div>

                        {/* Volume math */}
                        <div className="mt-6">
                            <GlassCard>
                                <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 py-2">
                                    <div className="text-center">
                                        <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-1">Today's Volume</p>
                                        <p className="text-3xl md:text-4xl font-black text-white tracking-tight">818</p>
                                        <p className="text-white/40 text-xs mt-1">pieces / month</p>
                                    </div>
                                    <ArrowRight className="text-purple-300 rotate-90 md:rotate-0" size={28} />
                                    <div className="text-center">
                                        <p className="text-emerald-300 text-[10px] uppercase tracking-widest font-semibold mb-1">Phase 2 Volume</p>
                                        <p className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-purple-300 tracking-tight">~1,200</p>
                                        <p className="text-white/40 text-xs mt-1">pieces / month (+47%)</p>
                                    </div>
                                </div>
                                <p className="text-white/40 text-xs text-center mt-5 max-w-2xl mx-auto">
                                    Main page at 5/day plus five fan pages at 7/day is <span className="text-white/70 font-semibold">40 pieces a day</span>, each distributed across Instagram Reels, YouTube Shorts &amp; TikTok — a +47% jump on June, the highest-volume month the network has ever run.
                                </p>
                            </GlassCard>
                        </div>
                    </div>
                </section>

                {/* PROJECTED REACH */}
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-5">
                        <GlassCard className="flex flex-col justify-center">
                            <p className="text-white/40 text-[11px] uppercase tracking-widest font-semibold mb-2">Projected monthly reach</p>
                            <p className="text-5xl md:text-6xl font-black text-emerald-300 tracking-tighter">≈ 670K<span className="text-lg text-white/50 font-semibold"> views / mo</span></p>
                            <p className="text-white/40 text-sm mt-4 leading-relaxed">
                                Projection only — 1,200 pieces × the network's measured {PROOF.avgViewsPerPiece} views/piece.
                                Not a guarantee; views depend on the algorithm. Volume is what we commit to.
                            </p>
                        </GlassCard>
                        <GlassCard>
                            <p className="text-white font-bold text-lg mb-4 tracking-tight">The compounding logic</p>
                            <ul className="space-y-3">
                                {[
                                    ['More pages', '= more shots on goal for the next viral persona like Unseen.'],
                                    ['More cadence', '= faster algorithm incubation on every account.'],
                                    ['Stronger main page', '= brand deals land on your flagship, not a fan page.'],
                                    ['Same backlog', '= the marginal cost of volume is low; the upside isn\'t.'],
                                ].map(([a, b], i) => (
                                    <li key={i} className="flex gap-3 text-sm text-white/50">
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-400 flex-none" />
                                        <span><span className="text-white font-semibold">{a}</span> {b}</span>
                                    </li>
                                ))}
                            </ul>
                        </GlassCard>
                    </div>
                </section>

                {/* INVESTMENT */}
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-5xl">
                        <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold text-center mb-3">The Investment</p>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">More than double the machine.</h2>
                        <p className="text-white/50 text-center mb-12 max-w-2xl mx-auto">
                            Phase 1 ran a 4-page network. Phase 2 is 6 pages at full cadence — more than twice the output, one flat monthly rate.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
                            <GlassCard className="flex flex-col justify-center">
                                <p className="text-purple-300 text-[11px] uppercase tracking-widest font-semibold mb-3">Network Phase 2 · monthly</p>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-6xl font-black text-white tracking-tighter">$2,500</span>
                                    <span className="text-white/50 font-semibold text-lg">/ month</span>
                                </div>
                                <p className="text-white/40 text-sm mt-3">3-month term · $7,500 total · billed monthly</p>
                                <div className="mt-6 pt-6 border-t border-white/10">
                                    <p className="text-white/40 text-xs leading-relaxed">
                                        Third-party tool subscriptions (Later, Poppy AI, ManyChat) billed separately, or run on our seats at no charge — same as Phase 1.
                                    </p>
                                </div>
                            </GlassCard>

                            <GlassCard>
                                <p className="text-white font-bold text-lg mb-5 tracking-tight">Everything in Phase 2</p>
                                <ul className="space-y-3.5">
                                    {INCLUDES.map(([a, b], i) => (
                                        <li key={i} className="flex gap-3 items-start">
                                            <span className="flex-none mt-0.5 w-5 h-5 rounded-md bg-emerald-500/15 text-emerald-300 grid place-items-center"><Check size={13} strokeWidth={3} /></span>
                                            <span className="text-sm text-white leading-snug"><span className="font-semibold">{a}.</span> {b && <span className="text-white/50">{b}</span>}</span>
                                        </li>
                                    ))}
                                </ul>
                            </GlassCard>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-16 px-4 pb-28">
                    <div className="container mx-auto max-w-3xl text-center">
                        <GlassCard>
                            <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold mb-3">Next Step</p>
                            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 tracking-tight">Say go, and the two new pages start incubating this week.</h2>
                            <p className="text-white/50 max-w-xl mx-auto">
                                Confirm Phase 2 and we roll straight into the highest-volume month the network has run — no restart, no cold-start.
                            </p>
                        </GlassCard>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
